/**
 * Product data fetching and transformation utilities
 * Retrieves and formats product pricing and variant information from Storefront API
 */

import { globalState } from '@/components/helpers/utils'
import { QUERY_PRODUCT_PRICE, buildProductAvailabilityQuery } from './queries'
import { storefront } from 'libs/storefront/client'

/**
 * Product interface representing the structure of product data
 */
interface Product {
  handle: string;
  variants: Array<any>;
  [key: string]: any;
}

/**
 * ProductPrice interface for the simplified product price structure
 */
interface ProductPrice {
  title: string;
  handle: string;
  price: string;
}

/**
 * Fetches product prices for a given country
 * @param results - Array of product results to get prices for
 * @returns Promise containing simplified product prices or null if error
 */
async function getProductPrices(results: Product[]): Promise<ProductPrice[] | null> {
  // Validate inputs
  if (!Array.isArray(results) || results.length === 0) {
    console.error('Invalid or empty results array')
    return null
  }

  // Check for country code
  if (!globalState?.localization?.country) {
    console.warn('Country code not found in global state')
    return null
  }

  try {
    const country = globalState.localization.country
    const resultCount = results.length
    const query = getProductIDs(results)

    // Execute query
    const productPriceResponse = await storefront.query({
      query: QUERY_PRODUCT_PRICE,
      variables: {
        country,
        query,
        count: resultCount
      }
    })

    // Check for errors in response
    if (productPriceResponse.errors) {
      throw new Error(JSON.stringify(productPriceResponse.errors))
    }

    // Check if response contains expected data structure
    if (!productPriceResponse.data?.products?.edges) {
      throw new Error('Unexpected response structure')
    }

    return simplifyProductPrices(productPriceResponse)
  } catch (error) {
    console.error('Error fetching product prices:', error)
    return null
  }
}

/**
 * ProductAvailability interface for the Availability Status metafield lookup
 */
interface ProductAvailability {
  handle: string;
  availabilityStatus: string | null;
  releaseDate: string | null;
}

/**
 * Fetches the Availability Status / Release Date metafields for a set of handles.
 * Used by the Quick Add gate (libs/athos/helpers/availability.ts) — the Athos
 * feed carries neither field, so a Coming Soon product is indistinguishable
 * from an in-stock one on the card data alone.
 *
 * Deliberately NOT `@inContext`-scoped: metafield values don't vary by market,
 * and adding the directive would fragment the request by country for no gain.
 *
 * @param handles - Product handles to look up (batch size is capped by the caller)
 * @returns One row per requested handle, in the order asked for, or null on error
 */
async function getProductAvailability(handles: string[]): Promise<ProductAvailability[] | null> {
  if (!Array.isArray(handles) || handles.length === 0) return null

  const cleaned = handles.map((handle) => String(handle)).filter(Boolean)
  if (!cleaned.length) return null

  try {
    // Handles travel as GraphQL variables, so they need no escaping and can't
    // reshape the document.
    const variables: Record<string, string> = {}
    cleaned.forEach((handle, index) => {
      variables[`h${index}`] = handle
    })

    const response = await storefront.query({
      query: buildProductAvailabilityQuery(cleaned.length),
      variables,
      // `helpers/availability.ts` keeps the resolved map that actually matters;
      // letting Apollo normalise and retain one document per batch shape on top
      // of it is pure memory growth on a long PLP session.
      fetchPolicy: 'no-cache'
    })

    if (response.errors) {
      throw new Error(JSON.stringify(response.errors))
    }

    const data: any = response.data ?? {}

    // Mapped back by index rather than by the returned `handle`, so a product
    // the API doesn't know about (unpublished, renamed) still yields a row for
    // the handle that was asked for instead of silently dropping out.
    return cleaned.map((handle, index) => {
      const node = data[`p${index}`]
      return {
        handle,
        availabilityStatus: node?.availabilityStatus?.value ?? null,
        releaseDate: node?.releaseDate?.value ?? null
      }
    })
  } catch (error) {
    console.error('Error fetching product availability:', error)
    return null
  }
}

/**
 * Extracts product handles and joins them with OR for the query
 * @param results - Array of product results
 * @returns String of product handles joined with OR operator
 */
const getProductIDs = (results: Product[]): string => {
  if (!Array.isArray(results)) return ''

  const idArray = results.map((result: Product) => result.uid).filter(Boolean) // Filter out any undefined or empty handles

  return idArray.map((id: string) => `(id:"${id.split('/').pop()}")`).join(' OR ')
}

/**
 * Builds a query string for product handles
 * @param handles - String of product handles
 * @returns Formatted query string
 */
const buildQuery = (handles: string): string => {
  if (!handles) return ''
  return `handle:${handles}`
}

/**
 * Simplifies product price data from the API response
 * @param productPriceResponse - API response with product price data
 * @returns Array of simplified product price objects
 */
const simplifyProductPrices = (productPriceResponse: any): ProductPrice[] => {
  const edges = productPriceResponse?.data?.products?.edges || []

  return edges.map((product: any) => {
    const node = product?.node || {}
    const firstVariantEdge = node?.variants?.edges?.[0] || {}
    const firstVariantNode = firstVariantEdge?.node || {}

    const price = firstVariantNode?.price?.amount || '0'

    return {
      title: node.title || '',
      handle: node.handle || '',
      id: node.id || '',
      price
    }
  })
}

export { getProductPrices, getProductAvailability, getProductIDs, buildQuery, simplifyProductPrices }
